import { useState } from "react";
import { lookupSlug } from "@/lib/slugLookup";
import { Navigate } from "@/lib/router-compat";
import { useStableLocation } from "@/hooks/useStableLocation";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import CommercialBrowseSidebar from "@/components/services/CommercialBrowseSidebar";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import ContactCTA from "@/components/ContactCTA";
import WebinarRegistrationForm from "@/components/webinar/WebinarRegistrationForm";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { getServiceBySlug } from "@/data/servicesData";
import ServiceHero from "@/components/services/ServiceHero";
import ServiceWhyQbd from "@/components/services/ServiceWhyQbd";
import ServiceRelatedContent from "@/components/services/ServiceRelatedContent";
import GenericOfferingContent from "@/components/services/GenericOfferingContent";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import { heroTakeaway, keyTakeawaySchema } from "@/data/keyTakeaways";

import { toxOfferingData } from "@/data/toxOfferingData";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLanguage, langPrefix as getLangPrefix } from "@/hooks/useLanguage";
import { getToxOfferingMeta, getLocalizedToxOfferingData, getToxOfferingUI } from "@/i18n/toxOfferings";
import { getServiceContent, getServiceDetailT } from "@/i18n/serviceDetail";
import { localizeHref } from "@/lib/localizeHref";
import biocompatibilityHeroAsset from "@/assets/biocompatibility-biological-evaluation-hero.jpg.asset.json";

import { SITE_URL as SITE, DEFAULT_OG_IMAGE } from "@/config/site";

interface FaqItem { q: string; a: string; }

/* ── Spanish slug → English contentKey mapping ── */
const ES_SLUG_MAP: Record<string, string> = {
  "informes-pde-oel": "pde-oel-reports",
  "evaluación-de-riesgos-de-las-nitrosaminas": "nitrosamine-risk-assessment",
  "informes-sobre-extractables-y-lixiviables": "extractables-leachables-reports",
  "evaluación-de-riesgos-medioambientales-era": "environmental-risk-assessment-era",
  // The published Spanish URL for this page is unaccented.
  "evaluacion-de-riesgos-medioambientales-era": "environmental-risk-assessment-era",

  "informes-sobre-impurezas-elementales": "elemental-impurities-reports",
  "informes-sobre-impurezas-orgánicas": "organic-impurities-reports",
  "informes-sobre-disolventes-residuales": "residual-solvents-reports",
  "registro-reach-y-apoyo-al-cumplimiento": "reach-registration-compliance-support",
  "estudios-de-seguimiento": "monitoring-studies",
};

const getOfferingSlug = (pathname: string): string | null => {
  // Match both /en/services/regulatory-toxicology/xxx and /es/servicios/toxicologia-regulatoria/xxx
  const match = pathname.match(/\/(?:services\/regulatory-toxicology|servicios\/toxicologia-regulatoria)\/([^/]+)$/);
  if (!match) return null;
  const raw = decodeURIComponent(match[1]);
  return lookupSlug(ES_SLUG_MAP, raw);
};

const offeringPages: Record<string, {
  title: string;
  subtitle: string;
  description: string;
  contentKey: string;
  ogImage?: string;
  faqItems?: FaqItem[];
  contentTags?: string[];
  /** Optional exact meta title (bypasses the "— Toxicology | QbD Group" suffix) */
  metaTitle?: string;
  /** Optional exact meta description */
  metaDescription?: string;
  /** Optional offering-specific contact banner / form copy */
  formHeading?: string;
  formSubtitle?: string;
}> = {

  "pde-oel-reports": {
    title: "PDE-OEL Reports",
    subtitle: "Specialized toxicological support backed by accredited experts",
    description: "QbD Group prepares PDE/ADE reports for any Active Pharmaceutical Ingredient in compliance with EMA directives. Our ERT-certified toxicologists deliver scientifically robust assessments for the pharmaceutical and veterinary industries.",
    contentKey: "pde-oel-reports",
    contentTags: ["Toxicology", "Pharma & Biotech"],
    faqItems: [
      { q: "What is a PDE report?", a: "A PDE (Permitted Daily Exposure) report establishes the maximum acceptable daily exposure to a specific substance, based on a comprehensive toxicological evaluation of the compound." },
      { q: "How many compounds has QbD evaluated?", a: "Our team has evaluated over 3,500 compounds for PDE/ADE reports, serving more than 600 clients across 50+ countries." },
      { q: "Who prepares and reviews the reports?", a: "All reports are prepared and reviewed by ERT-certified toxicologists with extensive experience in pharmaceutical toxicology." },
    ],
  },
  "nitrosamine-risk-assessment": {
    title: "Nitrosamine Risk Assessment",
    subtitle: "Navigate nitrosamine regulatory complexities with expert guidance",
    description: "QbD Group specializes in nitrosamine risk assessments, helping pharmaceutical companies identify risks, write reports, calculate acceptable limits, and address regulatory agency responses.",
    contentKey: "nitrosamine-risk-assessment",
    contentTags: ["Toxicology", "Pharma & Biotech"],
    faqItems: [
      { q: "What are nitrosamines, and why are they a concern?", a: "Nitrosamines are chemical compounds that may pose a cancer risk. Regulatory agencies require their presence in pharmaceutical products to be minimized or eliminated." },
      { q: "What products require these evaluations?", a: "According to EMA: MAHs/Applicants of all human medicinal products shall assess the risk of presence of nitrosamine impurities in their API(s) and FP(s)." },
      { q: "How long does the risk assessment take?", a: "Depending on complexity, assessments typically take 4-8 weeks." },
      { q: "What happens if there is a risk of nitrosamine impurities?", a: "If a risk is identified, the first step is confirmatory testing to experimentally determine nitrosamine levels. Depending on results, appropriate actions will be taken." },
      { q: "What regulatory guidelines do you follow?", a: "We adhere to EMA and FDA guidelines specific to nitrosamines, ICH Q9 principles for risk assessment, ICH M7, and other global regulations." },
      { q: "Is it possible to mitigate risk using QSAR-based strategies?", a: "No, in-silico data is not currently accepted for determining limits or assessing the extent of nitrosamine formation." },
    ],
  },
  "extractables-leachables-reports": {
    title: "Extractables & Leachables Reports",
    subtitle: "Expert toxicological assessments for packaging and device safety",
    description: "QbD Group provides toxicological assessments and risk evaluations for extractable and leachable impurities, designed to fit your specific needs and ensure compliance with FDA, EMA, and ICH standards.",
    contentKey: "extractables-leachables-reports",
    contentTags: ["Toxicology", "Medical Devices"],
    faqItems: [
      { q: "Why are extractables and leachables studies important?", a: "These studies ensure that impurities do not compromise the safety, efficacy, or quality of the product, while meeting international regulatory requirements." },
      { q: "What regulations apply to extractables and leachables?", a: "FDA, EMA, USP 1663, USP 1664, and ICH Q3E guidelines require rigorous evaluations of these impurities." },
      { q: "When should these studies be performed?", a: "They should be conducted during product development and before commercialization." },
      { q: "What products require these evaluations?", a: "All pharmaceutical products in contact with plastics, rubber, metals, or other packaging and device components." },
    ],
  },
  "environmental-risk-assessment-era": {
    title: "Environmental Risk Assessment (ERA)",
    subtitle: "Mandatory ERA reports aligned with EMA regulatory expectations",
    description: "QbD Group supports pharmaceutical companies throughout the full ERA lifecycle, combining scientific expertise with a practical, risk-based approach to deliver submission-ready documentation for Marketing Authorization Applications.",
    contentKey: "environmental-risk-assessment-era",
    contentTags: ["Toxicology", "Regulatory Affairs"],
  },
  "biocompatibility-biological-evaluation": {
    title: "Biocompatibility & Biological Evaluation Solutions",
    subtitle: "Biological safety is not just a box to check, it is a regulatory critical step towards approval",
    description: "Biocompatibility is the ability of a medical device to perform its intended function without causing an unacceptable adverse biological response in the human body. As medical device regulations become increasingly risk-based and scientifically demanding, biological evaluation is a strategic component of regulatory success for a smooth conformity assessment. With ISO 10993-1:2025 introducing a significantly revised, risk-management-oriented framework, manufacturers need more than a testing laboratory, they need the right scientific and regulatory strategy from the outset.",
    contentKey: "biocompatibility-biological-evaluation",
    contentTags: ["Toxicology", "Medical Devices"],
    metaTitle: "Biocompatibility & Biological Evaluation Solutions | QbD Group",
    metaDescription: "QbD Group supports medical device manufacturers with end-to-end biocompatibility and biological evaluation under ISO 10993, from feasibility to submission-ready BER.",
    formHeading: "Let's talk Biocompatibility",
    formSubtitle: "From feasibility assessment to submission-ready BER, our biocompatibility experts are ready to support your medical device program.",
  },

  "elemental-impurities-reports": {
    title: "Elemental Impurities Reports",
    subtitle: "Comprehensive risk assessments for pharmaceutical compliance",
    description: "QbD Group provides comprehensive risk assessments and reports for elemental impurities in human and veterinary pharmaceutical products, ensuring compliance with ICH Q3D guidelines and regulatory requirements.",
    contentKey: "elemental-impurities-reports",
    contentTags: ["Toxicology", "Pharma & Biotech"],
  },
  "organic-impurities-reports": {
    title: "Organic Impurities Reports",
    subtitle: "Certified toxicology reports for impurity evaluations",
    description: "QbD Group offers the largest online library of high-quality toxicology reports certified by AETOX members, covering risk assessments and toxicological evaluations for organic impurities in pharmaceutical manufacturing.",
    contentKey: "organic-impurities-reports",
    contentTags: ["Toxicology", "Pharma & Biotech"],
  },
  "residual-solvents-reports": {
    title: "Residual Solvents Reports",
    subtitle: "Risk assessments for residual solvents in pharmaceutical products",
    description: "QbD Group specializes in risk assessments for residual solvents in human and veterinary drug products, helping identify risks, generate comprehensive reports, and address regulatory concerns per ICH Q3C guidelines.",
    contentKey: "residual-solvents-reports",
    contentTags: ["Toxicology", "Pharma & Biotech"],
  },
  "reach-registration-compliance-support": {
    title: "REACH Registration & Compliance",
    subtitle: "Navigate REACH registration with expert guidance",
    description: "QbD Group helps you navigate every step of REACH registration, from substance characterization and IUCLID dossier build to SDS authoring and Chemical Safety Reporting.",
    contentKey: "reach-registration-compliance-support",
    contentTags: ["Toxicology", "Regulatory Affairs"],
  },
  "monitoring-studies": {
    title: "Monitoring Studies",
    subtitle: "Expert support for laboratory studies and regulatory compliance",
    description: "QbD Group provides expert support for follow-up of laboratory studies required in the manufacture and quality of medicines, including method validation, results review, and report writing for regulatory submissions.",
    contentKey: "monitoring-studies",
    contentTags: ["Toxicology", "Lab Services"],
  },
};

const ToxOffering = () => {
  const location = useStableLocation();
  const [formOpen, setFormOpen] = useState(false);
  const lang = useLanguage();

  const slug = getOfferingSlug(location.pathname);
  const page = slug ? offeringPages[slug] : undefined;
  const parentService = getServiceBySlug("toxicology");

  if (!page || !parentService) {
    const fallbackPath = lang === "es" ? "/es/servicios/toxicologia-regulatoria" : `${getLangPrefix(lang)}/services/regulatory-toxicology`;
    return <Navigate to={fallbackPath} replace />;
  }

  const ui = getToxOfferingUI(lang);
  const i18nMeta = getToxOfferingMeta(page.contentKey, lang);

  const displayTitle = i18nMeta?.title ?? page.title;
  const displaySubtitle = i18nMeta?.subtitle ?? page.subtitle;
  const displayDescription = i18nMeta?.description ?? page.description;
  const displayFaqItems = i18nMeta?.faqItems ?? page.faqItems;

  const serviceI18n = getServiceContent("toxicology", lang);

  const heroService = {
    ...parentService,
    title: displayTitle,
    subtitle: displaySubtitle,
    description: displayDescription,
    path: location.pathname,
    heroImage: page.contentKey === "biocompatibility-biological-evaluation"
      ? biocompatibilityHeroAsset.url
      : parentService.heroImage,
  };

  
  const pageOgImage = page.ogImage || DEFAULT_OG_IMAGE;
  const metaTitle = page.metaTitle ?? `${displayTitle} — ${ui.toxicology} | QbD Group`;
  const metaDesc = page.metaDescription ?? displayDescription.slice(0, 155);

  const pageUrl = `${SITE}${location.pathname}`;
  const relatedTags = page.contentTags || ["Toxicology"];

  const rawData = toxOfferingData[page.contentKey];
  const data = rawData ? getLocalizedToxOfferingData(page.contentKey, rawData, lang) : undefined;

  const formHeading = (lang === "en" ? page.formHeading : undefined) ?? serviceI18n?.formHeading ?? parentService.formHeading;
  const formSubtitle = (lang === "en" ? page.formSubtitle : undefined) ?? serviceI18n?.formSubtitle ?? parentService.formSubtitle;

  const langPrefix = getLangPrefix(lang);
  const servicesPath = localizeHref("/en/services", lang);
  const toxServicePath = lang === "es" ? "/es/servicios/toxicologia-regulatoria" : `${langPrefix}/services/regulatory-toxicology`;

  return (
    <>
      <SEOHead
        title={metaTitle}
        description={metaDesc}
        canonical={pageUrl}
        ogImage={pageOgImage}
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: displayTitle,
            description: displayDescription,
            provider: { "@type": "Organization", name: "QbD Group", url: SITE },
            url: pageUrl,
            isPartOf: { "@type": "Service", name: ui.toxicology, url: `${SITE}${toxServicePath}` },
            ...keyTakeawaySchema(heroTakeaway(displaySubtitle, displayDescription)),
          })}
        </script>
        {displayFaqItems && displayFaqItems.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: displayFaqItems.map((faq) => ({
                "@type": "Question",
                name: faq.q,
                acceptedAnswer: { "@type": "Answer", text: faq.a },
              })),
            })}
          </script>
        )}
      </SEOHead>

      
      <BreadcrumbJsonLd
        items={[
          { name: getServiceDetailT(lang).services, path: servicesPath },
          { name: ui.toxicology, path: toxServicePath },
          { name: displayTitle },
        ]}
      />

      <ScrollToTop />
      <Navbar />
      <CommercialBrowseSidebar />

      <main className="pb-16 sm:pb-0" style={{ '--service-color': parentService.color } as React.CSSProperties}>
        <ServiceHero
          service={heroService}
          onCtaClick={() => setFormOpen(true)}
          hideDiscoverButton={page.contentKey !== "biocompatibility-biological-evaluation"}
          discoverButtonLabel="Read more"
          discoverButtonTarget="service-offerings"
          parentCrumbs={[
            { label: ui.toxicology, to: toxServicePath },
          ]}
        />

        {data && <GenericOfferingContent data={data} onCtaClick={() => setFormOpen(true)} hidePartnerSection />}

        <ServiceWhyQbd
          service={parentService}
          onCtaClick={() => setFormOpen(true)}
          partnerOverride={data ? { intro: data.partnerIntro, bullets: data.partnerBullets } : undefined}
        />

        {displayFaqItems && displayFaqItems.length > 0 && (
          <section className="py-16 sm:py-20 bg-muted/30">
            <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-6 h-0.5 rounded-full" style={{ backgroundColor: parentService.color }} />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{({ en: "Risk assessment requirements", es: "Requisitos de evaluaci\u00f3n", "zh-cn": "风险评估实施要求" }[lang])}</p>
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-6">{ui.faqTitle}</h2>
              <Accordion type="single" collapsible className="space-y-2">
                {displayFaqItems.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="bg-background border border-border/40 rounded-lg px-5">
                    <AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

        <ServiceRelatedContent service={parentService} contentTags={relatedTags} />

        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogTitle className="sr-only">{formHeading}</DialogTitle>
            <WebinarRegistrationForm
              registrationUrl={null}
              title={parentService.title}
              hubspotFormId={parentService.hubspotFormId}
              heading={formHeading}
              subtitle={formSubtitle}
            />
          </DialogContent>
        </Dialog>

        <ContactCTA
          onContactClick={() => setFormOpen(true)}
          title={formHeading}
          description={formSubtitle}
        />
      </main>

      <Footer />
    </>
  );
};

export default ToxOffering;
